import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser, AuthProvider } from "../types/index.js";
import { MockAuthProvider } from "../types/index.js";

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * Auth context value
 */
const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Get the auth provider instance based on provider type environment variable
 * For Phase 0, always returns MockAuthProvider
 */
function getProviderInstance(providerType: string): AuthProvider {
  switch (providerType) {
    case "mock":
      return new MockAuthProvider();
    case "supabase":
      // Placeholder for Phase 2
      throw new Error(
        "Supabase auth provider not yet implemented. Set VITE_AUTH_PROVIDER=mock"
      );
    default:
      // Default to mock for development
      console.warn(
        `Unknown auth provider type: ${providerType}, defaulting to mock`
      );
      return new MockAuthProvider();
  }
}

/**
 * Props for AuthContextProvider
 */
interface AuthContextProviderProps {
  children: ReactNode;
  /**
   * Override for auth provider type. If not specified, uses VITE_AUTH_PROVIDER env var.
   * Defaults to 'mock' if neither is set.
   */
  providerType?: "mock" | "supabase";
}

/**
 * AuthContextProvider - Provides authentication state to the React tree
 *
 * Wraps children with auth context, auto-authenticates with the configured provider,
 * and exposes auth state through the useAuth hook.
 *
 * Per architecture decision D12 (Swappable AuthProvider), provider selection is
 * based on the AUTH_PROVIDER environment variable.
 */
export function AuthContextProvider({
  children,
  providerType,
}: AuthContextProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-login for development (mock provider)
  useEffect(() => {
    const effectiveProvider = providerType || "mock";
    // Check local storage for session
    const storedToken = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("auth_user");
    
    if (storedToken && storedUser) {
        try {
            const parsedUser = JSON.parse(storedUser);
            // Validation: Ensure tenantId exists (critical for auth middleware)
            if (!parsedUser.tenantId) {
                console.warn("Stored user invalid (missing tenantId). Clearing session.");
                throw new Error("Invalid stored user");
            }
            setToken(storedToken);
            setUser(parsedUser);
        } catch (e) {
            console.error("Failed to parse/validate stored user", e);
            localStorage.removeItem("auth_user");
            localStorage.removeItem("auth_token");
        }
        return; // Return if valid, otherwise continue to auto-login below
    }

    // Auto-login dev user if no session and in mock mode
    if (effectiveProvider === "mock" && !user && !token) {
      // Use the seed credentials
      signIn("user@example.com", "password123").catch(e => console.error("Auto-login failed:", e));
    }
  }, [providerType]); // Run once on mount (or provider debug change)

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid credentials");
      }

      const data = await response.json();
      setUser(data.user);
      setToken(data.token);
      
      // Persist session
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_user", JSON.stringify(data.user));
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/api/auth/login`, { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
    }
  };

  const value: AuthState = {
    user,
    token,
    isAuthenticated: user !== null,
    isLoading,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth - Hook to access authentication state and methods
 *
 * Returns auth state including user, isAuthenticated, isLoading,
 * and signIn/signOut methods.
 *
 * Must be used within an AuthContextProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, isLoading, signIn, signOut } = useAuth();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (!isAuthenticated) return <div>Not authenticated</div>;
 *
 *   return <div>Welcome, {user?.email}</div>;
 * }
 * ```
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
}
